import { getLists } from 'api/list';
import { List } from 'api/list/graphqlTypes';
import fetchPhraseIfNeeded from 'utils/fetch-phrase-if-needed';
import {
    isMultipleItemsPayload,
    isRemoveFromListPayload,
    MultipleItemPayload,
    publish,
    REMOVE_REQUEST,
    SAVE_FOR_LATER_REMOVE_ITEMS_INITIATE,
    SAVE_FOR_LATER_REMOVE_ITEMS_REQUEST,
    SAVE_FOR_LATER_REMOVE_ITEMS_REQUEST_FAIL,
    SAVE_FOR_LATER_REMOVE_REQUEST,
} from 'utils/pubsub';
import { reportError } from 'utils/sentry';
import translate from 'utils/translate';
import {
    removeItemFromList,
    removeItemsFromSavedForLaterList,
} from '../../api/operations/removeFromLists/removeFromLists';

const handleRemoveFromListSubscription = (payload: unknown) => {
    if (!isRemoveFromListPayload(payload)) {
        return Promise.reject('Invalid payload');
    }
    return fetchPhraseIfNeeded().then(() =>
        Promise.all([import('./bootRemoveFromList'), getLists()])
            .then(([{ boot }, res]) => {
                if (res.data.lists.length <= 1) {
                    const itemQuantity =
                        res.data.lists[0]?.items.find(
                            item => item.itemNo === payload.productNumber
                        )?.quantity || 0;
                    return removeItemFromList({
                        payload: {
                            ...payload,
                            quantity: payload.quantity || '1',
                            productType: payload.productType || '',
                        },
                        quantity: itemQuantity,
                    });
                }

                return boot(res.data.lists as List[], {
                    ...payload,
                    quantity: payload.quantity || '1',
                    productType: payload.productType || '',
                });
            })
            .catch(e => {
                console.error(e);
                reportError(e as Error);
                throw Error('Favs-agent: Could not remove from favourites');
            })
    );
};

const handleRemoveFromSavedForLaterSubscription = async (payload: unknown) => {
    if (!isRemoveFromListPayload(payload)) {
        return Promise.reject('Invalid payload');
    }
    await fetchPhraseIfNeeded();
    const {
        data: { lists },
    } = await getLists();
    const savedForLaterList = lists.find(
        list => list.name === translate('SAVED_FOR_LATER_LIST_NAME')
    );
    if (savedForLaterList) {
        const itemQuantity =
            savedForLaterList.items.find(
                item => item.itemNo === payload.productNumber
            )?.quantity || 0;

        return removeItemFromList({
            payload: {
                ...payload,
                quantity: payload.quantity || '1',
                productType: payload.productType || '',
            },
            quantity: itemQuantity,
            savedForLaterListId: savedForLaterList.listId,
        });
    }
};

const handleRemoveItemsFromSavedForLaterSubscription = async (
    payload: MultipleItemPayload
) => {
    if (!isMultipleItemsPayload(payload)) {
        return Promise.reject('Invalid payload');
    }
    publish(SAVE_FOR_LATER_REMOVE_ITEMS_INITIATE, payload);
    await fetchPhraseIfNeeded();
    const {
        data: { lists },
    } = await getLists();
    const savedForLaterList = lists.find(
        list => list.name === translate('SAVED_FOR_LATER_LIST_NAME')
    );
    if (savedForLaterList) {
        return removeItemsFromSavedForLaterList({
            payload,
            savedForLaterListId: savedForLaterList.listId,
        });
    } else {
        publish(SAVE_FOR_LATER_REMOVE_ITEMS_REQUEST_FAIL, {
            ...payload,
            skipToast: payload.skipSavedForLaterToast,
        });
    }
};

export const initRemoveFromList = () => {
    window.ikea.pubsub.subscribe(
        REMOVE_REQUEST,
        handleRemoveFromListSubscription
    );
    window.ikea.pubsub.subscribe(
        SAVE_FOR_LATER_REMOVE_REQUEST,
        handleRemoveFromSavedForLaterSubscription
    );
    window.ikea.pubsub.subscribe(
        SAVE_FOR_LATER_REMOVE_ITEMS_REQUEST,
        handleRemoveItemsFromSavedForLaterSubscription
    );
    return handleRemoveFromListSubscription;
};
